import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useActiveEntity } from "@/lib/entity-context";
import { Loader2, ListTodo } from "lucide-react";

interface QuickTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultName?: string;
  defaultCustomerId?: string;
  defaultSourceType?: string;
  defaultSourceId?: string;
  defaultNotes?: string;
}

export function QuickTaskDialog({
  open,
  onOpenChange,
  defaultName = "",
  defaultCustomerId,
  defaultSourceType,
  defaultSourceId,
  defaultNotes = "",
}: QuickTaskDialogProps) {
  const { t } = useTranslation("common");
  const { toast } = useToast();
  const { activeEntityId } = useActiveEntity();
  const [name, setName] = useState(defaultName);
  const [notes, setNotes] = useState(defaultNotes);
  const [priority, setPriority] = useState("medium");
  const [deadline, setDeadline] = useState("");
  const [type, setType] = useState("follow_up");

  useEffect(() => {
    if (open) {
      setName(defaultName);
      setNotes(defaultNotes);
      setPriority("medium");
      setDeadline("");
      setType("follow_up");
    }
  }, [open, defaultName, defaultNotes]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        name,
        type,
        status: "open",
        priority,
        notes: notes || null,
        deadline: deadline || null,
        customerId: defaultCustomerId || null,
        sourceType: defaultSourceType || null,
        sourceId: defaultSourceId || null,
        entityId: activeEntityId || null,
      };
      const res = await apiRequest("POST", "/api/tasks", payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard"] });
      toast({ title: t("quickActions.taskCreated", "Task created") });
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast({
        title: t("quickActions.taskError", "Failed to create task"),
        description: err.message,
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-quick-task">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="text-quick-task-title">
            <ListTodo className="h-4 w-4" aria-hidden="true" />
            {t("quickActions.createTask", "Create Task")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
              {t("quickActions.taskName", "Task name")}
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("quickActions.taskNamePlaceholder", "What needs to be done?")}
              data-testid="input-quick-task-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                {t("quickActions.type", "Type")}
              </Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="min-h-[44px]" data-testid="select-quick-task-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="follow_up" data-testid="option-type-follow-up">{t("quickActions.typeFollowUp", "Follow-up")}</SelectItem>
                  <SelectItem value="reminder" data-testid="option-type-reminder">{t("quickActions.typeReminder", "Reminder")}</SelectItem>
                  <SelectItem value="review" data-testid="option-type-review">{t("quickActions.typeReview", "Review")}</SelectItem>
                  <SelectItem value="other" data-testid="option-type-other">{t("quickActions.typeOther", "Other")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                {t("quickActions.priority", "Priority")}
              </Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="min-h-[44px]" data-testid="select-quick-task-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low" data-testid="option-priority-low">{t("quickActions.priorityLow", "Low")}</SelectItem>
                  <SelectItem value="medium" data-testid="option-priority-medium">{t("quickActions.priorityMedium", "Medium")}</SelectItem>
                  <SelectItem value="high" data-testid="option-priority-high">{t("quickActions.priorityHigh", "High")}</SelectItem>
                  <SelectItem value="urgent" data-testid="option-priority-urgent">{t("quickActions.priorityUrgent", "Urgent")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
              {t("quickActions.deadline", "Deadline")}
            </Label>
            <Input
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className="min-h-[44px]"
              data-testid="input-quick-task-deadline"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
              {t("quickActions.notes", "Notes")}
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("quickActions.notesPlaceholder", "Additional details...")}
              className="resize-none min-h-[80px]"
              data-testid="input-quick-task-notes"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="min-h-[44px]"
              data-testid="button-quick-task-cancel"
            >
              {t("quickActions.cancel", "Cancel")}
            </Button>
            <Button
              size="sm"
              disabled={!name.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate()}
              className="min-h-[44px]"
              data-testid="button-quick-task-create"
            >
              {createMutation.isPending && (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" aria-hidden="true" />
              )}
              {t("quickActions.create", "Create")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
